import React, { useCallback, useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Typography, CircularProgress } from '@mui/material';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useScrollbarStyles } from 'modules/common';
import {
  CollectorInstallError,
  Maybe,
  useCollectorSettingLazyQuery
} from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useRetryInstallationFlow } from '../hooks/useRetryCollectorInstallationFlow';
import { useCollectorSyncStatusContext } from '../contexts/CollectorSyncStatusContextProvider';
import { CollectorSyncing } from './CompanyCollectorSettingsDialog/CollectorSyncing';

type CollectorErrorListProps = {
  errors: Array<CollectorInstallError | null | undefined> | undefined | null;
  collectorId?: string;
  installId: string;
  retryURL?: Maybe<string>;
};

const useStyles = makeStyles((theme) => ({
  itemContainer: {
    minWidth: '500px',
    margin: theme.spacing(1, 0, 0, 0)
  },
  listContainer: {
    maxHeight: '240px',
    overflowY: 'auto',
    borderRadius: '4px',
    padding: theme.spacing(0, 2, 1.5, 2),
    backgroundColor: theme.palette.table?.header
  },
  errorContainer: {
    lineHeight: 'normal',
    display: 'flex',
    color: theme.palette.error.dark,
    padding: theme.spacing(1.3, 2),
    borderRadius: theme.spacing(0.5),
    margin: theme.spacing(0.5, 0, 1, 0),
    backgroundColor: theme.palette.error.light
  },
  errorBanner: {
    flex: 1
  },
  retryButtonContainer: {
    float: 'right',
    flexShrink: 0,
    alignItems: 'center'
  },
  retryButton: {
    minWidth: 'fit-content',
    lineHeight: 'normal',
    marginLeft: theme.spacing(1),
    color: theme.palette.error.dark,
    padding: '0px',
    '&:hover': {
      backgroundColor: theme.palette.error.light
    }
  },
  retryIndicator: {
    verticalAlign: 'middle',
    marginLeft: theme.spacing(1),
    color: theme.palette.error.dark
  },
  outerContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`
  },
  title: {
    marginTop: theme.spacing(1)
  },
  dataValue: {
    display: 'block'
  }
}));

export const CollectorErrorList: React.FC<CollectorErrorListProps> = ({
  errors,
  retryURL,
  collectorId,
  installId
}: CollectorErrorListProps) => {
  const { retryInstallationFlow } = useRetryInstallationFlow();
  const [getSettings, { data, loading }] = useCollectorSettingLazyQuery();
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [collectorErrors, setCollectorErrors] = useState<
    Array<CollectorInstallError | null | undefined> | undefined | null
  >(errors);
  const {
    collectorsSyncStatus,
    setCollectorSyncStatus
  } = useCollectorSyncStatusContext();

  const [isSyncing, setIsSyncing] = useState<boolean>(
    collectorsSyncStatus[installId] === 'InProgress'
  );

  useEffect(() => {
    if (collectorsSyncStatus[installId] === 'InProgress') setIsSyncing(true);
    else {
      getSettings({
        variables: { input: { installId } }
      });
    }
  }, [getSettings, installId, collectorsSyncStatus]);

  useEffect(() => {
    if (!loading && data?.collectorSetting?.item?.installStatus?.errors) {
      const errors = data.collectorSetting.item.installStatus.errors;
      setCollectorErrors(errors);

      setIsSyncing(false);
      setIsRetrying(false);
    }
  }, [
    data?.collectorSetting?.item?.installStatus?.errors,
    setCollectorErrors,
    setIsSyncing,
    setIsRetrying,
    loading
  ]);

  const onRetrySuccess = useCallback(() => {
    setCollectorSyncStatus(installId, 'InProgress');
    setCollectorErrors(null);
  }, [setCollectorSyncStatus, setCollectorErrors, installId]);

  const onRetryClicked = useCallback(async () => {
    setIsRetrying(true);
    if (retryURL) {
      try {
        const dataSet = await retryInstallationFlow({
          retryURL
        });
        dataSet?.data?.retryInstallationFlow?.status === 200
          ? onRetrySuccess()
          : setIsRetrying(false);
      } catch (e: any) {
        console.error(e.message);
        setIsRetrying(false);
      }
    }
  }, [retryInstallationFlow, retryURL, onRetrySuccess]);

  const classes = useStyles();
  const classesScrollbar = useScrollbarStyles();
  const genericError = useMessage(
    'collectorSetting.details.errorBanner.general'
  );

  if (!isSyncing && (!collectorErrors || collectorErrors.length < 1)) {
    return null;
  }

  return (
    <div
      className={classes.outerContainer}
      data-testid="setting-dialog-collectorErrorList"
    >
      <Typography className={classes.title} align="left" variant="h6">
        <FormattedMessage id={'collectorSetting.details.dialogTitle'} />
      </Typography>
      {isSyncing ? (
        <CollectorSyncing />
      ) : (
        collectorErrors &&
        collectorErrors.length > 0 && (
          <>
            <div className={classes.errorContainer}>
              <Typography
                className={classes.errorBanner}
                data-testid="errorBanner"
                align="left"
                variant="body2"
              >
                <FormattedMessage
                  id={`collectorSetting.details.errorBanner.${collectorId}`}
                  defaultMessage={genericError}
                />
              </Typography>
              {retryURL && (
                <div className={classes.retryButtonContainer}>
                  {isRetrying && (
                    <CircularProgress
                      data-testid="progressIndicator"
                      className={classes.retryIndicator}
                      size={14}
                    />
                  )}
                  <Button
                    data-testid="retryButton"
                    onClick={onRetryClicked}
                    disabled={isRetrying}
                    className={classes.retryButton}
                  >
                    <FormattedMessage id={'collectorSetting.details.retry'} />
                  </Button>
                </div>
              )}
            </div>
            <div
              className={classnames(
                classes.listContainer,
                classesScrollbar.scrollbar
              )}
            >
              {collectorErrors.map((data, index) => {
                return (
                  <div key={index} className={classes.itemContainer}>
                    <Typography data-testid="errorTitle" variant="body1">
                      {data?.title}
                    </Typography>
                    <Typography
                      data-testid="errorMessage"
                      variant="caption"
                      className={classes.dataValue}
                    >
                      {data?.message}
                    </Typography>
                  </div>
                );
              })}
            </div>
          </>
        )
      )}
    </div>
  );
};
